import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { SeoHead } from "@/components/resources/SeoHead";
import { Button } from "@/components/ui/button";
import { Percent, Users2, LinkIcon, ArrowRight } from "lucide-react";

const APPLY_URL = "https://netlift-app.getrewardful.com/signup";

const META = {
  title: "NetLift Affiliate Program | 30% Recurring Commission",
  description:
    "Join the NetLift Affiliate Program and earn 30% recurring commission for 12 months on eligible referred customer revenue.",
  robots: "index,follow",
  canonical: "https://netlift.app/affiliates",
  og_title: "NetLift Affiliate Program — 30% recurring for 12 months",
  og_description:
    "Refer businesses to NetLift and earn 30% of eligible customer revenue for their first 12 months.",
  og_url: "https://netlift.app/affiliates",
};

const WHY = [
  {
    icon: Percent,
    title: "30% recurring commission",
    body: "Earn commission on eligible referred customer revenue for 12 months.",
  },
  {
    icon: Users2,
    title: "Built for B2B audiences",
    body: "A strong fit for audiences interested in AI, SaaS, finance, productivity and business performance.",
  },
  {
    icon: LinkIcon,
    title: "Simple tracking",
    body: "Unique referral links and commission tracking powered by Rewardful.",
  },
];

const FIT = [
  "AI and technology creators",
  "CFO, finance and FP&A publishers",
  "B2B SaaS review and comparison sites",
  "Newsletters and communities",
  "Business and productivity creators",
  "Podcasts and YouTube channels",
  "SEO and software publishers",
  "Consultants with a relevant audience",
];

const STEPS = [
  { n: "1", title: "Join", body: "Apply to the NetLift Affiliate Program." },
  { n: "2", title: "Refer", body: "Share your unique NetLift affiliate link with your audience." },
  { n: "3", title: "Earn", body: "Receive 30% commission on eligible customer revenue for the first 12 months." },
];

const FAQ = [
  {
    q: "Who can join?",
    a: "Publishers, creators, newsletters, communities, reviewers and other audience owners relevant to NetLift's B2B customers.",
  },
  { q: "How much do I earn?", a: "30% of eligible referred customer revenue for their first 12 months." },
  { q: "How are referrals tracked?", a: "Through your unique affiliate link using Rewardful." },
  { q: "Can I refer my own business?", a: "No. Self-referrals are not eligible for commission." },
];

function ApplyButton({ testid, variant = "primary", children = "Become a NetLift Affiliate" }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-medium transition-colors";
  const styles =
    variant === "primary"
      ? "bg-editorial text-white hover:bg-editorial/90"
      : "border border-slate-300 bg-white text-slate-800 hover:border-editorial hover:text-editorial";
  return (
    <a
      href={APPLY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`${base} ${styles}`}
      data-testid={testid}
    >
      {children}
      <ArrowRight className="h-4 w-4" />
    </a>
  );
}

export default function Affiliates() {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead meta={META} />
      <Navbar />
      <main>
        {/* HERO */}
        <section className="border-b border-slate-100 bg-white">
          <div className="mx-auto max-w-4xl px-6 py-20 sm:py-24" data-testid="affiliates-hero">
            <span className="text-xs font-semibold uppercase tracking-widest text-editorial">
              NetLift Affiliate Program
            </span>
            <h1 className="mt-3 font-heading text-4xl font-semibold leading-tight text-navy sm:text-5xl lg:text-6xl">
              Earn 30% recurring commission with NetLift
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
              Refer businesses to NetLift and earn 30% of eligible customer revenue for their first
              12 months.
            </p>
            <div className="mt-8">
              <ApplyButton testid="hero-apply-cta" />
            </div>
            <p className="mt-4 text-xs font-medium uppercase tracking-widest text-slate-400">
              30% recurring · 12 months · B2B SaaS
            </p>
          </div>
        </section>

        {/* WHY JOIN */}
        <section className="mx-auto max-w-6xl px-6 py-20" data-testid="affiliates-why">
          <h2 className="font-heading text-3xl font-semibold text-navy sm:text-4xl">
            Turn your audience into recurring revenue
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {WHY.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-slate-200 bg-white p-7"
                data-testid={`why-card-${title.toLowerCase().split(" ")[0]}`}
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-editorial/10 text-editorial">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-navy">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="border-y border-slate-100 bg-white">
          <div className="mx-auto max-w-6xl px-6 py-20" data-testid="affiliates-fit">
            <h2 className="font-heading text-3xl font-semibold text-navy sm:text-4xl">A good fit for</h2>
            <ul className="mt-8 grid gap-x-8 gap-y-3 text-base text-slate-700 sm:grid-cols-2 lg:grid-cols-2">
              {FIT.map((f) => (
                <li key={f} className="flex items-start gap-3 border-b border-slate-100 py-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-editorial" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm text-slate-500">
              Consultants implementing, managing or reselling NetLift should use the separate{" "}
              <a href="/contact" className="underline decoration-slate-300 underline-offset-2 hover:text-editorial">
                Solution Partner Program
              </a>
              .
            </p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="mx-auto max-w-6xl px-6 py-20" data-testid="affiliates-how">
          <h2 className="font-heading text-3xl font-semibold text-navy sm:text-4xl">Three steps to earn</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {STEPS.map((s) => (
              <div key={s.n} className="rounded-2xl border border-slate-200 bg-white p-7">
                <span className="kpi-number font-heading text-4xl font-semibold text-editorial">{s.n}</span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <ApplyButton testid="how-apply-cta" />
          </div>
        </section>

        {/* COMMISSION BLOCK — dominant visual */}
        <section className="bg-navy" data-testid="affiliates-commission">
          <div className="mx-auto max-w-4xl px-6 py-24 text-center">
            <div
              className="kpi-number font-heading text-8xl font-semibold leading-none text-white sm:text-9xl"
              aria-label="30 percent"
            >
              30<span className="text-editorial">%</span>
            </div>
            <h2 className="mt-6 font-heading text-3xl font-semibold text-white sm:text-4xl">
              Recurring for 12 months
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300">
              Earn 30% of eligible revenue from every customer you successfully refer during their
              first year with NetLift.
            </p>
            <div className="mt-8 flex justify-center">
              <ApplyButton testid="commission-apply-cta">Join the Affiliate Program</ApplyButton>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mx-auto max-w-3xl px-6 py-20" data-testid="affiliates-faq">
          <h2 className="font-heading text-3xl font-semibold text-navy sm:text-4xl">Frequently asked</h2>
          <dl className="mt-8 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
            {FAQ.map(({ q, a }) => (
              <div key={q} className="px-6 py-5">
                <dt className="font-heading text-base font-semibold text-navy">{q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-slate-600">{a}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* FINAL CTA */}
        <section className="border-t border-slate-100 bg-white" data-testid="affiliates-final-cta">
          <div className="mx-auto max-w-3xl px-6 py-20 text-center">
            <h2 className="font-heading text-3xl font-semibold text-navy sm:text-4xl">
              Ready to earn with NetLift?
            </h2>
            <div className="mt-8 flex justify-center">
              <ApplyButton testid="final-apply-cta" />
            </div>
            <p className="mt-4 text-xs font-medium uppercase tracking-widest text-slate-400">
              30% recurring commission for 12 months
            </p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
